import React, { useEffect, useMemo, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { createHostBridge } from './sdk/hostBridge';
import { buildGuestSdkSource } from './sdk/guestSdk';
import { buildGuestSrcDoc, guestBootUrl } from './sdk/guestDocument';
import { capabilities } from './capabilities';
import { DB_DISCONNECT_ALL_COMMANDS, markExtensionDbConnectionsClosed } from './capabilities/db';
import { registerBridge, unregisterBridge, useExtensionStore } from './store';
import {
  HOST_TO_GUEST,
  DISPOSE_FLUSH_TIMEOUT_MS,
  DEFAULT_IDLE_TEARDOWN_MS,
  newFrameToken,
} from './sdk/protocol';

/**
 * The guest document, exactly as it is mounted. Exported so the isolation
 * tests assert against the real thing rather than a re-creation of it. It
 * holds no inline script — see sdk/guestDocument.js for why.
 */
export function buildSrcDoc(entrySource, frameToken) {
  return buildGuestSrcDoc([buildGuestSdkSource(frameToken), entrySource], guestBootUrl());
}

/**
 * Mounts one extension panel's sandboxed iframe and owns its full lifecycle:
 * lazy content build on first mount, instant freeze + display:none when
 * `isVisible` goes false, then a full teardown (dispose -> bounded flush
 * window -> `onDisposed`, which the parent uses to unmount this component
 * entirely and release the iframe) after `idleTeardownMs` of continued
 * invisibility. Reopening is always a fresh mount — no residual iframe is
 * kept around "just in case".
 */
export default function ExtensionHost({
  extensionId,
  entrySource,
  isVisible,
  grantedCapabilities = [],
  idleTeardownMs = DEFAULT_IDLE_TEARDOWN_MS,
  onTreePublish,
  onDisposed,
  onGuestError,
}) {
  const iframeRef = useRef(null);
  const bridgeRef = useRef(null);
  const idleTimerRef = useRef(null);
  const flushTimeoutRef = useRef(null);
  const wasVisibleRef = useRef(isVisible);
  // Read through a ref wherever teardown code captured a closure at mount
  // time (the [extensionId] effect's cleanup) — a capability granted after
  // mount must still force-disconnect on unmount.
  const grantedCapabilitiesRef = useRef(grantedCapabilities);
  grantedCapabilitiesRef.current = grantedCapabilities;
  const [crashed, setCrashed] = useState(false);
  // Per-mount shared secret, generated once and given to BOTH ends: inlined
  // into this iframe's srcdoc and required on every envelope the bridge
  // accepts or emits. See newFrameToken() for why it exists alongside the
  // event.source checks.
  const frameTokenRef = useRef(null);
  if (frameTokenRef.current === null) frameTokenRef.current = newFrameToken();

  const srcDoc = useMemo(
    () => buildSrcDoc(entrySource, frameTokenRef.current),
    [entrySource]
  );

  // Forceful backstop for native resources (e.g. leaked MySQL connections)
  // that must never survive a teardown, regardless of whether the guest's
  // own DISPOSE handler ran or flushed in time — fired immediately rather
  // than waiting on the flush window, since the flush window exists for the
  // extension's own state persistence, not for resource cleanup.
  // Sections this guest published into, so a teardown can un-green them.
  const publishedSectionsRef = useRef(new Set());
  const disconnectNativeResources = () => {
    for (const cap of grantedCapabilitiesRef.current) {
      const command = DB_DISCONNECT_ALL_COMMANDS[cap];
      if (command) invoke(command, { extensionId }).catch(() => {});
    }
    markExtensionDbConnectionsClosed(extensionId);
    useExtensionStore.getState().clearArrowTones([...publishedSectionsRef.current]);
  };

  const teardown = () => {
    if (flushTimeoutRef.current) return; // already tearing down
    disconnectNativeResources();
    bridgeRef.current?.postToGuest(HOST_TO_GUEST.DISPOSE, {});
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
      onDisposed?.();
    };
    flushTimeoutRef.current = setTimeout(finish, DISPOSE_FLUSH_TIMEOUT_MS);
    bridgeRef.current?.setOnDisposedAck?.(finish);
  };

  useEffect(() => {
    const iframeEl = iframeRef.current;
    let disposedAck = null;

    const bridge = createHostBridge({
      extensionId,
      iframeEl,
      frameToken: frameTokenRef.current,
      grantedCapabilities,
      capabilities,
      onTreePublish: (sectionId, nodes) => {
        publishedSectionsRef.current.add(sectionId);
        useExtensionStore.getState().publishTree(sectionId, nodes);
        onTreePublish?.(sectionId, nodes);
      },
      onTreeExpand: (sectionId, ids) => useExtensionStore.getState().requestTreeExpand(sectionId, ids),
      onGuestError: (message) => {
        setCrashed(true);
        onGuestError?.(message);
      },
      onGuestReady: () => bridge.postToGuest(HOST_TO_GUEST.INIT, {}),
      onGuestDisposedAck: () => disposedAck?.(),
    });
    bridge.setOnDisposedAck = (fn) => {
      disposedAck = fn;
    };
    bridgeRef.current = bridge;
    registerBridge(extensionId, bridge);

    return () => {
      unregisterBridge(extensionId);
      bridge.destroy();
      bridgeRef.current = null;
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
      // Covers a hard unmount that bypasses teardown()'s own DISPOSE flow
      // entirely (e.g. the parent removes ExtensionHost directly) — the
      // native-resource backstop must fire on every unmount, not just the
      // idle-teardown path.
      disconnectNativeResources();
    };
    // srcDoc intentionally excluded: it's derived from extensionId/entrySource,
    // which don't change for the lifetime of a mounted host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensionId]);

  const idleMsRef = useRef(idleTeardownMs);
  useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = isVisible;
    const graceChanged = idleMsRef.current !== idleTeardownMs;
    idleMsRef.current = idleTeardownMs;
    // Re-arm when the panel is hidden, and when the grace period is changed in
    // Settings while it is already hidden (the new value applies right away).
    if (isVisible === wasVisible && !(graceChanged && !isVisible)) return;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (isVisible) {
      bridgeRef.current?.postToGuest(HOST_TO_GUEST.RESUME, {});
      return;
    }
    if (wasVisible) bridgeRef.current?.postToGuest(HOST_TO_GUEST.FREEZE, {});
    // null = never tear down while hidden (the DB Manager's "Never" setting —
    // see dbIdleDisconnect.js). setTimeout(fn, Infinity) would fire at once.
    if (!Number.isFinite(idleTeardownMs)) return;
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      teardown();
    }, idleTeardownMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, idleTeardownMs]);

  // Permission revocation must block the next capability call immediately,
  // not just on the extension's next remount — the bridge's grant set is
  // otherwise a closure snapshot taken at mount time (see hostBridge.js).
  useEffect(() => {
    bridgeRef.current?.setGrantedCapabilities?.(grantedCapabilities);
  }, [grantedCapabilities]);

  if (crashed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full text-sm text-gray-500 dark:text-zinc-400 gap-2">
        <p>Extension crashed.</p>
        <button
          type="button"
          className="px-3 py-1 rounded bg-blue-600 text-white text-xs"
          onClick={() => {
            setCrashed(false);
            onDisposed?.();
          }}
        >
          Reload
        </button>
      </div>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      title={extensionId}
      sandbox="allow-scripts"
      srcDoc={srcDoc}
      style={{
        display: isVisible ? 'block' : 'none',
        width: '100%',
        height: '100%',
        border: 'none',
        flex: '1 1 auto',
      }}
    />
  );
}
